import { useState } from "react";
import { AdminOrgs, type Org } from "../../lib/apiTyped";
import { useResource } from "../../lib/useResource";
import { PageHeader } from "../../components/AppLayout";
import { DataTable, type Column } from "../../components/shared/DataTable";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/shared/Button";
import { OrgDetailModal } from "../../components/admin/OrgDetailModal";
import { fmtDate } from "../../lib/format";

export function AdminOrganizations() {
  const [search, setSearch] = useState("");
  const [openOrg, setOpenOrg] = useState<string | null>(null);
  const { data, loading, refetch } = useResource(
    () => AdminOrgs.list({ type: "all", pageSize: 100 }),
    [],
  );

  const allRows = data?.orgs ?? null;
  const rows = allRows?.filter((o) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (o.name?.toLowerCase().includes(q) ?? false) ||
      o.id.toLowerCase().includes(q)
    );
  }) ?? null;

  const columns: Column<Org>[] = [
    { header: "Name", render: (r) => r.name },
    { header: "Type", render: (r) => r.type },
    { header: "Tier", render: (r) => r.activeTier ?? "—" },
    { header: "Seats", render: (r) => String(r.seatCount ?? 0) },
    { header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { header: "ID", render: (r) => <code className="text-xs text-ink-3">{r.id}</code> },
    { header: "Created", render: (r) => fmtDate(r.createdAt) },
  ];

  return (
    <>
      <PageHeader title="Organizations" />
      <div className="mb-6">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or ID…"
          className="w-full max-w-md rounded-xl px-3 py-2.5 text-sm focus:outline-none"
          style={{
            backgroundColor: "rgba(255,255,255,0.6)",
            border: "1px solid rgba(0,0,0,0.14)",
            color: "#0f0f0f",
          }}
        />
      </div>

      <DataTable
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        columns={columns}
        rowActions={(r) => (
          <Button variant="ghost" size="sm" onClick={() => setOpenOrg(r.id)}>
            Open
          </Button>
        )}
        empty={{ title: "No organizations" }}
      />

      {openOrg && (
        <OrgDetailModal
          orgId={openOrg}
          onClose={() => setOpenOrg(null)}
          onChanged={() => {
            void refetch();
          }}
        />
      )}
    </>
  );
}
